import React, { useEffect, useState } from 'react';
import { Download, Play, Pause, Volume2, VolumeX, Maximize, Video } from 'lucide-react';

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export interface GalleryVideoBlockProps {
  title: string;
  streamUrl: string | null;
  downloadUrl: string | null;
  archiveUrl: string | null;
  downloadFileName: string;
  archiveDownloadFileName: string;
  archiveLabel: string;
  /** Секунда кадра для превью до первого воспроизведения (например 5). */
  previewTimeSec?: number;
}

export const GalleryVideoBlock: React.FC<GalleryVideoBlockProps> = ({
  title,
  streamUrl,
  downloadUrl,
  archiveUrl,
  downloadFileName,
  archiveDownloadFileName,
  archiveLabel,
  previewTimeSec,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoHover, setVideoHover] = useState(false);
  /** Превью-кадр показан, но в UI время/скроллбар остаются на 0:00. */
  const [previewActive, setPreviewActive] = useState(previewTimeSec != null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const playerRef = React.useRef<HTMLDivElement>(null);
  const previewActiveRef = React.useRef(previewTimeSec != null);
  const hasPlayedRef = React.useRef(false);
  const clickTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const setPreview = (active: boolean) => {
    previewActiveRef.current = active;
    setPreviewActive(active);
  };

  const clearIdleTimer = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const showControls = () => {
    setControlsVisible(true);
    clearIdleTimer();
    if (!isFullscreen) return;
    idleTimerRef.current = setTimeout(() => setControlsVisible(false), 2000);
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      const fs = document.fullscreenElement === playerRef.current;
      setIsFullscreen(fs);
      if (!fs) {
        clearIdleTimer();
        setControlsVisible(true);
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    if (isFullscreen) {
      setControlsVisible(true);
      clearIdleTimer();
      idleTimerRef.current = setTimeout(() => setControlsVisible(false), 2000);
    } else {
      clearIdleTimer();
      setControlsVisible(true);
    }
    return clearIdleTimer;
  }, [isFullscreen]);

  useEffect(() => {
    if (streamUrl) {
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(false);
      setPreview(previewTimeSec != null);
      hasPlayedRef.current = false;
    }
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      clearIdleTimer();
    };
  }, [streamUrl, previewTimeSec]);

  const applyPreviewFrame = () => {
    const video = videoRef.current;
    if (!video || previewTimeSec == null || hasPlayedRef.current) return;
    const t = Math.min(
      previewTimeSec,
      Number.isFinite(video.duration) ? Math.max(0, video.duration - 0.1) : previewTimeSec,
    );
    if (Math.abs(video.currentTime - t) > 0.05) {
      video.currentTime = t;
    }
    setCurrentTime(0);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
      return;
    }
    if (previewActiveRef.current && previewTimeSec != null) {
      video.currentTime = 0;
    }
    setPreview(false);
    hasPlayedRef.current = true;
    setCurrentTime(0);
    void video.play();
    setIsPlaying(true);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = parseFloat(e.target.value);
    if (videoRef.current && Number.isFinite(t)) {
      setPreview(false);
      hasPlayedRef.current = true;
      videoRef.current.currentTime = t;
      setCurrentTime(t);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    if (videoRef.current && Number.isFinite(v)) {
      videoRef.current.volume = v;
      videoRef.current.muted = v === 0;
      setVolume(v);
      setIsMuted(v === 0);
    }
  };

  const toggleFullscreen = () => {
    const root = playerRef.current;
    if (!root) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void root.requestFullscreen();
    }
  };

  /** Одинарный клик — play/pause, двойной — fullscreen (без ложного toggle). */
  const handleVideoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      return;
    }
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      togglePlay();
    }, 250);
  };

  const handleVideoDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    // снять выделение браузера (синяя подсветка)
    window.getSelection()?.removeAllRanges();
    toggleFullscreen();
  };

  /** Блокирует синее выделение при даблклике. */
  const preventSelectOnMultiClick = (e: React.MouseEvent) => {
    if (e.detail > 1) e.preventDefault();
  };

  const downloadVideo = () => {
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = downloadFileName;
      link.click();
    }
  };

  const scrubberTime = previewActive ? 0 : currentTime;
  const overlaysShown = !isFullscreen || controlsVisible;
  const showPauseHint = isPlaying && (isFullscreen ? overlaysShown : videoHover);

  return (
    <div className="elegant-card elegant-card-no-hover p-6 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: 'var(--gradient-main)' }}
        >
          <Video className="w-6 h-6 text-white" />
        </div>
        <h2 className="text-2xl md:text-3xl font-serif font-semibold" style={{ color: 'var(--color-text)' }}>
          {title}
        </h2>
      </div>

      {streamUrl ? (
        <>
          <div
            ref={playerRef}
            className={
              isFullscreen
                ? `relative overflow-hidden group/video select-none bg-black w-screen h-screen rounded-none mb-0 ${overlaysShown ? '' : 'cursor-none'}`
                : 'relative rounded-2xl overflow-hidden shadow-2xl mb-6 group/video select-none bg-black'
            }
            style={{ WebkitUserSelect: 'none', userSelect: 'none', WebkitTapHighlightColor: 'transparent' }}
            onMouseEnter={() => setVideoHover(true)}
            onMouseLeave={() => setVideoHover(false)}
            onMouseMove={() => {
              if (isFullscreen) showControls();
            }}
            onMouseDown={preventSelectOnMultiClick}
          >
            <video
              ref={videoRef}
              className={
                isFullscreen
                  ? `absolute inset-0 w-full h-full object-contain outline-none select-none ${overlaysShown ? 'cursor-pointer' : 'cursor-none'}`
                  : 'w-full aspect-video cursor-pointer outline-none select-none'
              }
              src={streamUrl}
              preload="metadata"
              playsInline
              controlsList="nodownload noplaybackrate"
              disablePictureInPicture
              onClick={handleVideoClick}
              onDoubleClick={handleVideoDoubleClick}
              onMouseDown={preventSelectOnMultiClick}
              onPlay={() => {
                setPreview(false);
                hasPlayedRef.current = true;
                setIsPlaying(true);
              }}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              onTimeUpdate={() => {
                if (previewActiveRef.current || !videoRef.current) return;
                setCurrentTime(videoRef.current.currentTime);
              }}
              onLoadedMetadata={() => {
                if (videoRef.current) setDuration(videoRef.current.duration);
                applyPreviewFrame();
              }}
              onLoadedData={applyPreviewFrame}
              onSeeked={() => {
                if (previewActiveRef.current) {
                  setCurrentTime(0);
                  return;
                }
                if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
              }}
              onDurationChange={() => videoRef.current && setDuration(videoRef.current.duration)}
            />
            {!isPlaying && (
              <div
                className={`absolute inset-0 flex items-center justify-center bg-black/30 select-none transition-opacity duration-300 ${
                  overlaysShown ? 'opacity-100 cursor-pointer' : 'opacity-0 pointer-events-none cursor-none'
                }`}
                onClick={handleVideoClick}
                onDoubleClick={handleVideoDoubleClick}
                onMouseDown={preventSelectOnMultiClick}
                aria-hidden
              >
                <div className="w-20 h-20 rounded-full bg-white/30 flex items-center justify-center backdrop-blur-sm">
                  <Play className="w-10 h-10 text-white ml-1" fill="currentColor" />
                </div>
              </div>
            )}
            {showPauseHint && (
              <div
                className={`absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none transition-opacity duration-300 ${
                  overlaysShown ? 'opacity-100' : 'opacity-0'
                }`}
                aria-hidden
              >
                <div className="w-20 h-20 rounded-full bg-white/30 flex items-center justify-center backdrop-blur-sm pointer-events-none">
                  <Pause className="w-10 h-10 text-white" />
                </div>
              </div>
            )}
            <div
              className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 md:p-3 flex items-center gap-2 z-10 transition-opacity duration-300 ${
                overlaysShown ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <input
                type="range"
                min={0}
                max={duration || 100}
                step={0.1}
                value={scrubberTime}
                onChange={handleSeek}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 min-w-0 h-2 rounded-full cursor-pointer accent-white [color-scheme:dark]"
              />
              <span className="text-white text-xs font-medium tabular-nums shrink-0 whitespace-nowrap">
                {formatTime(scrubberTime)} / {formatTime(duration)}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors backdrop-blur-sm shrink-0"
                title={isMuted ? 'Включить звук' : 'Выключить звук'}
              >
                {isMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                onClick={(e) => e.stopPropagation()}
                className="w-16 md:w-20 h-2 rounded-full cursor-pointer accent-white [color-scheme:dark] shrink-0"
                title="Громкость"
              />
              <button
                onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                className="p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors backdrop-blur-sm shrink-0"
                title="Полный экран"
              >
                <Maximize className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {downloadUrl && (
              <button
                onClick={downloadVideo}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-semibold transition-all hover:shadow-lg"
                style={{ background: 'var(--gradient-main)' }}
              >
                <Download className="w-5 h-5" />
                <span>Скачать видео</span>
              </button>
            )}
            {archiveUrl && (
              <a
                href={archiveUrl}
                download={archiveDownloadFileName}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:shadow-md"
                style={{
                  backgroundColor: 'var(--color-cream-light)',
                  color: 'var(--color-text)',
                  borderWidth: '1px',
                  borderColor: 'var(--color-border)',
                }}
              >
                <Download className="w-5 h-5" />
                <span>{archiveLabel}</span>
              </a>
            )}
          </div>
        </>
      ) : (
        <p className="text-center py-8" style={{ color: 'var(--color-text-light)' }}>
          Видео пока нет в галерее
        </p>
      )}
    </div>
  );
};
